import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShoppingBag, ArrowLeft, Loader2, Package, KeyRound, RotateCcw, Copy, Camera, X, Link2 } from "lucide-react";
import { useCart } from "./cartContext";
import DeliveryMap from "./DeliveryMap";

const STATUS_BADGE = {
  pending:      "bg-slate-100 text-slate-700",
  confirmed:    "bg-blue-50 text-blue-700",
  dispatched:   "bg-amber-50 text-amber-700",
  awaiting_otp: "bg-fuchsia-50 text-fuchsia-700",
  delivered:    "bg-emerald-50 text-emerald-700",
  cancelled:    "bg-red-50 text-red-700",
  refunded:     "bg-fuchsia-50 text-fuchsia-700",
};

const STATUS_LABEL = {
  awaiting_otp: "Awaiting OTP",
};

export default function ShopOrders() {
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reorderBusy, setReorderBusy] = useState(null);
  const [proofPhoto, setProofPhoto] = useState(null);   // for the zoom overlay
  const { add } = useCart();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/store/orders");
      setOrders(data || []);
    } catch (err) {
      if (err?.response?.status === 401) nav("/shop/login");
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => { load(); }, [load]);

  // Poll every 10s while any order is awaiting the OTP so the customer sees
  // the code as soon as the delivery person taps "Arrived".
  useEffect(() => {
    const needsPoll = orders.some(
      (o) => o.status === "dispatched" || o.status === "awaiting_otp",
    );
    if (!needsPoll) return undefined;
    const id = window.setInterval(load, 10000);
    return () => window.clearInterval(id);
  }, [orders, load]);

  const copyOtp = async (otp) => {
    try {
      await navigator.clipboard.writeText(otp);
      toast.success("Code copied");
    } catch { /* ignore */ }
  };

  const reorder = async (o) => {
    setReorderBusy(o.id);
    try {
      const { data } = await api.post(`/store/orders/${o.id}/reorder`);
      const list = data?.available || [];
      if (list.length === 0) {
        toast.error("Sorry, none of these items are available right now.");
        return;
      }
      list.forEach((line) => {
        add(
          { id: line.item_id, name: line.name, price: line.price, images: [line.image] },
          line.quantity,
        );
      });
      const gone = (data?.unavailable || []).length;
      if (gone > 0) {
        toast(`Added ${list.length} of ${list.length + gone} items — ${gone} no longer available.`);
      } else {
        toast.success(`Added ${list.length} item${list.length === 1 ? "" : "s"} to your cart`);
      }
      nav("/shop/checkout");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reorder");
    } finally {
      setReorderBusy(null);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <Link
          to="/shop"
          className="inline-flex items-center gap-1.5 bg-[var(--shop-primary,#0055FF)] text-white hover:bg-[var(--shop-primary,#0040CC)] font-semibold text-sm px-3 py-1.5 rounded-md shadow-sm transition-transform hover:-translate-x-0.5"
          data-testid="shop-orders-back-to-shop"
        >
          <ArrowLeft className="w-4 h-4 stroke-[3]" />
          Shop
        </Link>
        <div className="flex items-center gap-2">
          <ShoppingBag className="w-5 h-5 text-[var(--shop-primary,#0055FF)]" />
          <span className="font-heading font-bold">Order history</span>
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-4 md:p-6 space-y-3">
        {orders.length === 0 ? (
          <Card className="border-slate-200 shadow-none">
            <CardContent className="p-8 text-center text-slate-500 text-sm">
              <Package className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              You haven&apos;t placed any orders yet.
            </CardContent>
          </Card>
        ) : (
          orders.map((o) => (
            <Card key={o.id} className="border-slate-200 shadow-none" data-testid={`order-${o.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="mono text-[11px] text-slate-500">
                    Order · {new Date(o.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </div>
                  <span className={"mono text-[10px] px-2 py-0.5 rounded uppercase " + (STATUS_BADGE[o.status] || "bg-slate-100")}>
                    {STATUS_LABEL[o.status] || o.status}
                  </span>
                </div>

                {/* OTP surface — the delivery person will ask the customer for
                    this 4-digit code to close the delivery. */}
                {o.status === "awaiting_otp" && o.delivery_otp && (
                  <div
                    className="mb-3 border-2 border-fuchsia-300 bg-fuchsia-50 rounded-md p-3 flex items-center gap-3"
                    data-testid={`order-otp-${o.id}`}
                  >
                    <KeyRound className="w-5 h-5 text-fuchsia-700 shrink-0" />
                    <div className="flex-1">
                      <div className="mono text-[10px] uppercase tracking-widest text-fuchsia-700 font-semibold">
                        Delivery code — read it out to the delivery person
                      </div>
                      <div className="mono text-3xl font-bold tracking-[0.3em] text-fuchsia-900 mt-0.5">
                        {o.delivery_otp}
                      </div>
                    </div>
                    <button
                      onClick={() => copyOtp(o.delivery_otp)}
                      className="mono text-[10px] px-2 py-1 rounded bg-fuchsia-700 text-white hover:bg-fuchsia-800 flex items-center gap-1"
                      data-testid={`order-otp-copy-${o.id}`}
                    >
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                  </div>
                )}

                {/* Public tracking link — the customer can copy this and
                    open it even after logging out. Keeps the OTP reachable
                    when their session drops mid-delivery. Only shown for
                    orders that still have "active" states worth tracking. */}
                {o.tracking_token && o.status !== "cancelled" && (
                  <button
                    type="button"
                    onClick={() => {
                      const url = `${window.location.origin}/shop/track/${o.id}?t=${encodeURIComponent(o.tracking_token)}`;
                      navigator.clipboard?.writeText(url);
                      toast.success("Tracking link copied — save it to check your order without logging in");
                    }}
                    className="mb-3 w-full text-left text-[11px] mono flex items-center gap-2 px-3 py-2 rounded-md bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-600 hover:text-slate-800 transition-colors"
                    data-testid={`order-share-${o.id}`}
                  >
                    <Link2 className="w-3.5 h-3.5 text-[var(--shop-primary,#0055FF)]" />
                    <span className="flex-1 truncate">
                      Share this order — no login needed
                    </span>
                    <span className="text-[10px] uppercase tracking-widest text-slate-500">
                      Copy link
                    </span>
                  </button>
                )}

                {/* Live delivery map — active while the employee is on the way. */}
                {(o.status === "dispatched" || o.status === "awaiting_otp") && (
                  <div className="mb-3">
                    <DeliveryMap orderId={o.id} />
                  </div>
                )}

                {/* Delivery proof photo — snapped by the delivery person at
                    the moment of OTP verification. Shown to the customer as
                    reassurance the package really arrived at their door. */}
                {o.status === "delivered" && o.delivery_proof_photo && (
                  <button
                    type="button"
                    onClick={() => setProofPhoto(o.delivery_proof_photo)}
                    className="w-full mb-3 rounded-md overflow-hidden border border-emerald-200 relative group focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    data-testid={`order-proof-${o.id}`}
                  >
                    <img
                      src={o.delivery_proof_photo}
                      alt="Doorstep delivery proof"
                      className="w-full h-40 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/70 via-transparent to-transparent flex items-end justify-between p-2">
                      <span className="mono text-[10px] uppercase tracking-widest text-white bg-emerald-600 px-2 py-0.5 rounded shadow flex items-center gap-1">
                        <Camera className="w-3 h-3" /> Doorstep proof
                      </span>
                      <span className="text-white/90 text-[11px] group-hover:underline">Tap to zoom</span>
                    </div>
                  </button>
                )}

                <ul className="space-y-1 mb-2">
                  {o.items.map((it, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      {it.image ? <img src={it.image} alt="" className="w-8 h-8 rounded object-cover" /> : <div className="w-8 h-8 bg-slate-100 rounded" />}
                      <span className="flex-1 truncate">{it.name}</span>
                      <span className="mono text-xs text-slate-500">×{it.quantity}</span>
                      <span className="mono">₹{it.line_total.toLocaleString("en-IN")}</span>
                    </li>
                  ))}
                </ul>
                <div className="border-t border-slate-100 pt-2 flex justify-between text-sm">
                  <span className="text-slate-500">Shipping to {o.pin_code}</span>
                  <span className="mono">₹{Number(o.shipping_charge).toLocaleString("en-IN")}</span>
                </div>
                <div className="flex justify-between text-sm font-semibold pt-1">
                  <span>Total</span>
                  <span className="mono">₹{Number(o.total).toLocaleString("en-IN")}</span>
                </div>

                {/* Reorder — one tap to drop the whole basket back into cart. */}
                {(o.status === "delivered" || o.status === "cancelled") && (
                  <div className="border-t border-slate-100 pt-3 mt-3 flex justify-end">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => reorder(o)}
                      disabled={reorderBusy === o.id}
                      className="h-8 text-[var(--shop-primary,#0055FF)] border-[var(--shop-primary,#0055FF)] hover:bg-blue-50"
                      data-testid={`order-reorder-${o.id}`}
                    >
                      {reorderBusy === o.id
                        ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                        : <RotateCcw className="w-3.5 h-3.5 mr-1" />}
                      Reorder these items
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Zoom overlay for doorstep proof photos */}
      {proofPhoto && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setProofPhoto(null)}
          data-testid="proof-photo-overlay"
        >
          <button
            type="button"
            onClick={() => setProofPhoto(null)}
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white rounded-full p-2"
            aria-label="Close"
            data-testid="proof-photo-close"
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={proofPhoto}
            alt="Doorstep delivery proof"
            className="max-w-full max-h-full rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
